"use client";

/**
 * @file Search Analyticsプロジェクトのソート可能なサイドバーコンポーネント
 * 統合基底コンポーネントを使用した軽量な実装
 */

import { Trans, useLingui } from "@lingui/react/macro";
import { type FragmentOf, readFragment } from "gql.tada";
import { FileTextIcon, Loader2Icon, SearchIcon } from "lucide-react";
import Link from "next/link";
import { toast } from "sonner";
import { useClickAction } from "@/app/[lang]/_hooks/use-click-action";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SA_POLICIES } from "@/lib/authorization/policies/search-analytics";
import { useCheckPolicy } from "@/lib/authorization/use-check-policy";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../../_contexts/workspace-sidebar-data-context";
import { createSearchAnalyticsOnboardingIntent } from "../../../action";
import { serializeSearchAnalyticsOnboardingSearchParams } from "../../../onboarding/search-analytics/_components/onboarding-helpers";
import { createDragOverlayProject } from "./drag-overlay-utils";
import { SearchAnalyticsProjectItemFragment } from "./fragments";
import ProjectSidebarItem from "./project-sidebar-item";
import { SaSidebarSortable } from "./sa-sidebar-sortable";
import { SortableSidebarGroupsBase } from "./sortable-sidebar-groups-base";
import type { SAProject } from "./types";

interface SortableSAProjectsProps {
  workspaceId: string;
  projectsData: readonly FragmentOf<
    typeof SearchAnalyticsProjectItemFragment
  >[];
  currentPathname: string;
  onboardingSessions: ReadonlyArray<{
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  }>;
  completedOnboardingSessions: ReadonlyArray<{
    projectID: string;
    status: string;
    latestSnapshotID?: string | null;
  }>;
}

/**
 * Search Analyticsプロジェクト用ソート可能サイドバーコンポーネント
 * 統合基底コンポーネントを使用してコードの重複を削減
 */
export default function SaSortableProjects({
  workspaceId,
  projectsData,
  currentPathname,
  onboardingSessions,
  completedOnboardingSessions,
}: SortableSAProjectsProps) {
  const checkPolicy = useCheckPolicy();
  const canEditProjects = checkPolicy(SA_POLICIES["sa-project:edit"]).ok;

  // GraphQL Fragmentデータをプロジェクト型に変換
  const projects: SAProject[] = projectsData
    .map((fragmentData) => {
      const project = readFragment(
        SearchAnalyticsProjectItemFragment,
        fragmentData,
      );
      return project
        ? {
            id: project.id,
            name: project.name,
            color: project.color,
          }
        : null;
    })
    .filter((project): project is SAProject => project !== null);
  const latestCompletedOnboardingSessionByProjectId = new Map<
    string,
    (typeof completedOnboardingSessions)[number]
  >();
  for (const session of completedOnboardingSessions) {
    if (
      session.projectID &&
      session.status === "APPLIED" &&
      !latestCompletedOnboardingSessionByProjectId.has(session.projectID)
    ) {
      latestCompletedOnboardingSessionByProjectId.set(
        session.projectID,
        session,
      );
    }
  }
  const latestUnfinishedOnboardingSessionByProjectId = new Map<
    string,
    (typeof onboardingSessions)[number]
  >();
  for (const session of onboardingSessions) {
    if (
      session.projectID &&
      session.status !== "APPLIED" &&
      !latestUnfinishedOnboardingSessionByProjectId.has(session.projectID)
    ) {
      latestUnfinishedOnboardingSessionByProjectId.set(
        session.projectID,
        session,
      );
    }
  }

  // SA専用のレンダリング関数
  const renderSAProject = (
    project: SAProject & { isProjectActive: boolean },
    isDragging: boolean,
  ) => {
    const pathPattern = `.*/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(project.id)}/(board|list)`;
    const linkPath = `/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(project.id)}`;

    // 対応するフラグメントデータを検索
    const fragmentData = projectsData.find((data) => {
      const p = readFragment(SearchAnalyticsProjectItemFragment, data);
      return p?.id === project.id;
    });

    const onboardingSession = latestUnfinishedOnboardingSessionByProjectId.get(
      project.id,
    );
    const completedOnboardingSession =
      latestCompletedOnboardingSessionByProjectId.get(project.id);

    // SA固有のサイドバーコンポーネント
    const sidebarContent = fragmentData ? (
      <SaSidebarSortable
        project={fragmentData}
        workspaceId={workspaceId}
        onboardingSession={onboardingSession}
      />
    ) : null;

    return (
      <ProjectSidebarItem
        projectId={project.id}
        projectName={project.name}
        projectColor={project.color || "#000000"}
        projectType="sa"
        workspaceId={workspaceId}
        isProjectActive={project.isProjectActive || Boolean(onboardingSession)}
        isDragging={isDragging}
        linkHref={linkPath}
        activePattern={pathPattern}
        additionalMenuItems={
          !onboardingSession && completedOnboardingSession ? (
            <ViewInitialAnalysisMenuItem
              workspaceId={workspaceId}
              projectId={completedOnboardingSession.projectID}
            />
          ) : canEditProjects && !onboardingSession ? (
            <StartInitialAnalysisMenuItem
              workspaceId={workspaceId}
              projectId={project.id}
            />
          ) : undefined
        }
      >
        {sidebarContent}
      </ProjectSidebarItem>
    );
  };

  // DragOverlay用のレンダリング関数
  const renderSAProjectForDragOverlay = (
    project: SAProject & { isProjectActive: boolean },
  ) => {
    // 対応するフラグメントデータを検索
    const fragmentData = projectsData.find((data) => {
      const p = readFragment(SearchAnalyticsProjectItemFragment, data);
      return p?.id === project.id;
    });

    const onboardingSession = latestUnfinishedOnboardingSessionByProjectId.get(
      project.id,
    );

    // SA固有のサイドバーコンポーネント
    const sidebarContent = fragmentData ? (
      <SaSidebarSortable
        project={fragmentData}
        workspaceId={workspaceId}
        onboardingSession={onboardingSession}
      />
    ) : null;

    return createDragOverlayProject({
      projectId: project.id,
      projectName: project.name,
      projectColor: project.color || "#000000",
      projectType: "sa",
      workspaceId,
      children: sidebarContent,
    });
  };

  return (
    <SortableSidebarGroupsBase
      workspaceId={workspaceId}
      items={projects}
      projectType="sa"
      currentPathname={currentPathname}
      renderItem={renderSAProject}
      renderDragOverlayItem={renderSAProjectForDragOverlay}
      disableSorting={!canEditProjects}
    />
  );
}

function ViewInitialAnalysisMenuItem({
  workspaceId,
  projectId,
}: {
  workspaceId: string;
  projectId: string;
}) {
  return (
    <DropdownMenuItem
      onSelect={(event) => {
        event.stopPropagation();
      }}
      asChild
    >
      <Link
        href={serializeSearchAnalyticsOnboardingSearchParams(
          `/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(projectId)}/onboarding`,
          { step: "results" },
        )}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <FileTextIcon className="mr-2 h-4 w-4" />
        <Trans>View initial analysis</Trans>
      </Link>
    </DropdownMenuItem>
  );
}

function StartInitialAnalysisMenuItem({
  workspaceId,
  projectId,
}: {
  workspaceId: string;
  projectId: string;
}) {
  const { t } = useLingui();
  const router = useRouter();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const [, startInitialAnalysis, isPending] = useClickAction(
    async () => {
      const result = await createSearchAnalyticsOnboardingIntent(
        workspaceId,
        projectId,
      );
      if (result.errors.length === 0) {
        refreshWorkspaceSidebar("onboarding-mutation");
        router.push(
          `/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(projectId)}/onboarding`,
        );
        router.refresh();
      } else {
        toast.error(t`Failed to start initial analysis`, {
          description: result.errors.join(", "),
        });
      }
      return result;
    },
    { errors: [] },
  );

  return (
    <DropdownMenuItem
      disabled={isPending}
      onSelect={(event) => {
        event.preventDefault();
        event.stopPropagation();
        startInitialAnalysis();
      }}
    >
      {isPending ? (
        <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <SearchIcon className="mr-2 h-4 w-4" />
      )}
      <Trans>Start initial analysis</Trans>
    </DropdownMenuItem>
  );
}
